"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HighlightedText,
  type HighlightTone,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  CheckerInput,
  MarkedSentence,
  ToneLegend,
} from "@/components/tools/style-checkers-a/a-frame";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  findVaguePronouns,
  type VagueKind,
  type VagueMatch,
} from "@/lib/tool-kit/style-checkers/vague-pronouns";

/** English strings. A translated content file overrides these through `ui`. */
export const VAGUE_PRONOUN_FINDER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, a section or a whole paper…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "Enrolment dropped by a third in the first year. This worries the department. The sample was small, which limits generalisability. Participants were tired, and this reduced accuracy. Several measures changed. These suggest the scale is sensitive. This study examines three schools. The method, which is simple, works well.\n\nIt had flaws that the authors did not discuss. Scores varied a lot. Which is why we report medians.",
  showPossible: "Show possible flags too",
  ignoreQuotes: "Ignore quoted text",
  enterPrompt:
    "Paste or type some text to see its vague pronouns — this, that, these, those, which and it with nothing clear for them to point at.",
  results: "Results",
  likely: "Likely vague",
  possible: "Possible",
  opening: "Sentences opening with a bare This/That",
  sentences: "Sentences checked",
  words: "Words",
  judgement:
    "A heuristic, not a verdict: it flags the patterns that make readers guess. Whether a pronoun is really vague depends on the sentence before it, so you make the call.",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with vague pronouns highlighted",
  legendLikely: "Likely vague",
  legendPossible: "Possible",
  hoverHint:
    "Hover over a highlight to see why it was flagged; the cards below suggest nouns to try.",
  noneFound:
    "No vague pronouns found. That does not mean none exist: the checker cannot tell which of two nouns “it” means.",
  onlyPossible:
    "Only possible flags were found, and they are hidden. Tick “Show possible flags too” to see them.",
  findingsHeading: "Each flagged pronoun",
  confidenceLikely: "Likely",
  confidencePossible: "Possible",
  tryHeading: "Name what it points to",
  tryHint:
    "Pick the noun that fits what you mean; these are starting points, not answers.",
  byPronounHeading: "Flags by pronoun",
  colPronoun: "Pronoun",
  colCount: "Flags",
  explainDemonstrativeStart:
    "A sentence that opens with “{pronoun}” and a verb leaves the reader to decide what it points to: the whole last sentence, one idea in it, or the paragraph. Adding the noun settles it.",
  explainDemonstrativeMid:
    "“{pronoun}” stands alone after a conjunction, so it could point at the whole clause before it or at one noun inside it.",
  explainWhichBroad:
    "“which” after a comma, followed by this verb, usually refers to the whole clause before it rather than to one noun. Say what it refers to, or start a new sentence.",
  explainWhichStart:
    "“Which” opening a statement has no clause to attach to. Join it to the sentence before it, or begin with “This …” and name the thing.",
  explainItFirst:
    "“It” opens a paragraph, so there is no earlier sentence on the page to match it to. Name the subject.",
} as const;

type Strings = typeof VAGUE_PRONOUN_FINDER_STRINGS;

const TONES: Record<string, HighlightTone> = {
  likely: "rose",
  possible: "amber",
};

const EXPLAIN: Record<VagueKind, keyof Strings> = {
  "demonstrative-start": "explainDemonstrativeStart",
  "demonstrative-mid": "explainDemonstrativeMid",
  "which-broad": "explainWhichBroad",
  "which-start": "explainWhichStart",
  "it-first": "explainItFirst",
};

function explain(match: VagueMatch, strings: Strings): string {
  return t(strings[EXPLAIN[match.kind]], { pronoun: match.text });
}

function FindingCard({
  match,
  strings,
}: {
  match: VagueMatch;
  strings: Strings;
}) {
  return (
    <li className={`${CARD} space-y-3`}>
      <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
        {match.confidence === "likely"
          ? strings.confidenceLikely
          : strings.confidencePossible}
      </p>
      <MarkedSentence
        from={match.start - match.sentenceStart}
        sentence={match.sentence}
        to={match.end - match.sentenceStart}
        tone={TONES[match.confidence]}
      />
      <p className={HINT}>{explain(match, strings)}</p>
      {match.ideas.length > 0 ? (
        <div className="space-y-1">
          <p className="font-medium text-foreground text-sm">
            {strings.tryHeading}
          </p>
          <ul className="flex flex-wrap gap-2">
            {match.ideas.map((idea) => (
              <li
                className="rounded-md border border-border bg-muted/40 px-2 py-1 text-foreground text-sm"
                key={idea}
              >
                {idea}
              </li>
            ))}
          </ul>
          <p className={HINT}>{strings.tryHint}</p>
        </div>
      ) : null}
    </li>
  );
}

export default function VaguePronounFinderWidget() {
  const strings = useToolUi(VAGUE_PRONOUN_FINDER_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [showPossible, setShowPossible] = useState(true);
  const [ignoreQuotes, setIgnoreQuotes] = useState(true);
  const deferred = useDeferredValue(text);
  const report = useMemo(
    () => findVaguePronouns(deferred, { ignoreQuotes }),
    [deferred, ignoreQuotes]
  );
  const shown = report.matches.filter(
    (match) => showPossible || match.confidence === "likely"
  );
  const highlights: TextMatch[] = shown.map((match) => ({
    start: match.start,
    end: match.end,
    category: match.confidence,
    title: explain(match, strings),
  }));
  const hasText = deferred.trim().length > 0;

  return (
    <div className="space-y-6">
      <CheckerInput
        clearLabel={strings.clear}
        example={strings.exampleText}
        exampleLabel={strings.tryExample}
        extra={
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Checkbox
              checked={showPossible}
              label={strings.showPossible}
              onChange={setShowPossible}
            />
            <Checkbox
              checked={ignoreQuotes}
              label={strings.ignoreQuotes}
              onChange={setIgnoreQuotes}
            />
          </div>
        }
        hint={strings.privacy}
        id="vpf-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        value={text}
      />

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.likely}
                value={fmt(report.likely)}
              />
              <StatCard label={strings.possible} value={fmt(report.possible)} />
              <StatCard
                label={strings.opening}
                value={fmt(report.sentenceInitial)}
              />
              <StatCard
                label={strings.sentences}
                value={fmt(report.sentences)}
              />
              <StatCard label={strings.words} value={fmt(report.words)} />
            </StatGrid>
            <p className={HINT}>{strings.judgement}</p>
          </div>

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <ToneLegend
              items={[
                { tone: "rose", label: strings.legendLikely },
                { tone: "amber", label: strings.legendPossible },
              ]}
            />
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={TONES}
            />
            <p className={HINT}>{strings.hoverHint}</p>
          </div>

          {report.matches.length === 0 ? (
            <Notice>{strings.noneFound}</Notice>
          ) : null}
          {report.matches.length > 0 && shown.length === 0 ? (
            <Notice>{strings.onlyPossible}</Notice>
          ) : null}

          {shown.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.findingsHeading}</h3>
              <ul className="space-y-3">
                {shown.map((match) => (
                  <FindingCard
                    key={match.start}
                    match={match}
                    strings={strings}
                  />
                ))}
              </ul>
            </div>
          ) : null}

          {report.byPronoun.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.byPronounHeading}</h3>
              <DataTable
                head={[strings.colPronoun, strings.colCount]}
                rows={report.byPronoun.map((row) => [row.key, fmt(row.count)])}
              />
            </div>
          ) : null}
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
